import React from 'react';
import moment from 'moment';
import { AlignmentCard } from '../Alignment/Alignment.styles';
import { TruncatedText } from '../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { PendingItemDTO } from '../../../../../api/useOverviewQuery';
import Hourglass from '../../../../img/hourglass.svg';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { useResendInviteMutation } from '../../../../../api/useResendInviteMutation';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { MESSAGES } from '../../../../../../CommonConstants';
import { useRemoveItemMutation } from '../../../../../api/useRemoveItemMutation';
import { useAcceptRequestMutation } from '../../../../../api/useAcceptRequestMutation';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';

interface Props {
  type: 'invite' | 'request';
  item: PendingItemDTO;
}

export const PendingItem = ({ type, item }: Props) => {
  const { mutateAsync: resendInvite, isLoading: isResendingInvite } = useResendInviteMutation();
  const { mutateAsync: acceptInvite, isLoading: isAcceptingInvite } = useAcceptRequestMutation();
  const { mutateAsync: removeItem, isLoading: isRemovingItem } = useRemoveItemMutation();

  const clientId = useClientId();

  const handleAcceptRequest = async () => {
    try {
      await acceptInvite({ clientId, inviteId: item.id });
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const handleResendInvite = async () => {
    try {
      await resendInvite({ clientId, inviteId: item.id });

      toast.success(
        'Invitation sent. You will see the results in your comparisons when they have completed the assessment.',
      );
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const handleRemoveItem = async () => {
    try {
      await removeItem({ type, clientId, inviteId: item.id });
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  return (
    <AlignmentCard as="article" css="cursor: unset">
      {type === 'invite' && (
        <TruncatedText fontWeight={500}>{`Waiting for ${item.name}`}</TruncatedText>
      )}

      {type === 'request' && (
        <>
          <Text fontWeight={500}>Comparison Request</Text>
          <TruncatedText fontWeight={500}>{`from ${item.name}`}</TruncatedText>
        </>
      )}

      <TruncatedText
        color="neutral.500"
        fontWeight={500}
        fontSize="small"
        mb={type === 'invite' ? 32 : 12}
      >
        {`${type === 'invite' ? 'Invited' : 'Received'} ${moment.utc(item.dateInvited).fromNow()}`}
      </TruncatedText>

      <Hourglass />

      <Box display="flex" flexDirection="column" mt={14} alignSelf="stretch" css="gap: 8px">
        <Button
          variant={type === 'invite' ? 'secondary' : 'primary'}
          onClick={type === 'invite' ? handleResendInvite : handleAcceptRequest}
          isLoading={isResendingInvite || isAcceptingInvite}
          size="small"
        >
          {type === 'invite' ? 'Resend Invite' : 'Accept'}
        </Button>

        <Button
          variant="subtleInactive"
          size="small"
          onClick={handleRemoveItem}
          isLoading={isRemovingItem}
        >
          Remove
        </Button>
      </Box>
    </AlignmentCard>
  );
};
